<template>
  <div class="station-banner-wrapper">
    <div v-if="state.doesStationPdfExists" class="col-12 pb-5">
      <a :href="state.pdfLink" target="_blank">
        <img :src="bannerImgSrc" loading="lazy" class="w-100" />
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, watch } from "vue";
import { checkIfStationPdfExists } from "@/stationPdf/checkIfStationPdfExits.js";
import { CustomWindowEvents } from "@/enums/customWindowEvents";
import summerBannerDesktopImg from "@/images/bannerDesktopSummer.png";
import summerBannerMobileImg from "@/images/bannerMobileSummer.png";
import winterBannerDesktopImg from "@/images/bannerDesktopWinter.png";
import winterBannerMobileImg from "@/images/bannerMobileWinter.png";

type ScreenType = "desktop" | "tablet" | "mobile" | string;
type StationPdfLink = string;

const props = defineProps<{
  parentStationId?: string;
  stationId?: string;
}>();

const w = window as Window & {
  seasonManager?: { season?: string };
  screenSizeManager?: { screenType?: ScreenType };
};

const state = reactive({
  currentScreenSize: w.screenSizeManager?.screenType ?? ("desktop" as ScreenType),
  currentSeason: w.seasonManager?.season ?? "",
  doesStationPdfExists: false,
  pdfLink: "" as StationPdfLink,
});

const isCurrentSeasonSummer = computed(() => state.currentSeason === "E");

const isMobileScreenSize = computed(() => state.currentScreenSize === "mobile");

const bannerImgSrc = computed(() => {
  const isSummer = isCurrentSeasonSummer.value;
  const isMobile = isMobileScreenSize.value;

  if (isSummer) {
    return isMobile ? summerBannerMobileImg : summerBannerDesktopImg;
  }

  return isMobile ? winterBannerMobileImg : winterBannerDesktopImg;
});

async function getStationBannerPdf() {
  const pdfContentLink = await checkIfStationPdfExists({
    stationId: props.stationId,
    parentStationId: props.parentStationId,
    isSummer: isCurrentSeasonSummer.value,
  });

  if (pdfContentLink) {
    state.doesStationPdfExists = true;
    state.pdfLink = pdfContentLink;
  } else {
    state.doesStationPdfExists = false;
    state.pdfLink = "";
  }
}

function onScreenTypeChange() {
  const screenType = w.screenSizeManager?.screenType;
  if (screenType) state.currentScreenSize = screenType;
}

async function onSeasonChange() {
  state.currentSeason = w.seasonManager?.season ?? "";
  await getStationBannerPdf();
}

onMounted(async () => {
  state.currentSeason = w.seasonManager?.season ?? "";
  await getStationBannerPdf();
  window.addEventListener("screenTypeChange", onScreenTypeChange);
  window.addEventListener(CustomWindowEvents.CHANGE_SEASON, onSeasonChange);
});

watch(
  () => [props.stationId, props.parentStationId] as const,
  async () => {
    await getStationBannerPdf();
  }
);

onUnmounted(() => {
  window.removeEventListener("screenTypeChange", onScreenTypeChange);
  window.removeEventListener(CustomWindowEvents.CHANGE_SEASON, onSeasonChange);
});
</script>

<style scoped>

img {
  box-shadow: 0px 0px 7px rgb(0 0 0 / 20%);
  border-radius: 20px;
}
</style>
